import { formatOrderPriceRupees } from './domains/tradingDomain'

export type LadderRole = 'TARGET' | 'ENTRY' | 'LTP' | 'STOP'

export type LadderRow = {
    role: LadderRole
    label: string
    /** Null only for a MARKET entry with no known LTP. */
    price: number | null
    priceLabel: string
    /** Signed distance from the entry reference, e.g. "+3.5%"; absent on the entry row. */
    distanceLabel?: string
}

export type BracketLadder = {
    rows: LadderRow[]
    riskRupees: number | null
    rewardRupees: number | null
    /** "1 : 2.0" (reward per unit of risk), when both sides are known. */
    ratioLabel: string | null
}

export type BracketLadderInput = {
    side: string
    quantity: number
    entryLimitPrice: number | null
    entryIsMarket: boolean
    stopPrice: number | null
    targetPrice: number | null
    ltpRupees: number | null
}

function distanceLabel(price: number, reference: number | null): string | undefined {
    if (reference == null || reference <= 0) return undefined
    const pct = ((price - reference) / reference) * 100
    const sign = pct > 0 ? '+' : pct < 0 ? '−' : ''
    return `${sign}${Math.abs(pct).toFixed(1)}%`
}

/**
 * Price-ordered view of a bracket (highest first): target / entry / LTP / stop, with distances from the
 * entry reference (limit price, or LTP for a market entry) and rupee risk / reward for the whole quantity.
 */
export function buildBracketLadder(input: BracketLadderInput): BracketLadder {
    const isBuy = input.side.toUpperCase() === 'BUY'
    const entryRef = input.entryLimitPrice ?? (input.entryIsMarket ? input.ltpRupees : null)

    const rows: LadderRow[] = []
    rows.push({
        role: 'ENTRY',
        label: input.entryIsMarket ? 'Entry · market' : 'Entry',
        price: entryRef,
        priceLabel: entryRef != null ? formatOrderPriceRupees(entryRef) : 'Market'
    })
    if (input.targetPrice != null) {
        rows.push({
            role: 'TARGET',
            label: 'Target',
            price: input.targetPrice,
            priceLabel: formatOrderPriceRupees(input.targetPrice),
            distanceLabel: distanceLabel(input.targetPrice, entryRef)
        })
    }
    if (input.stopPrice != null) {
        rows.push({
            role: 'STOP',
            label: 'Stop',
            price: input.stopPrice,
            priceLabel: formatOrderPriceRupees(input.stopPrice),
            distanceLabel: distanceLabel(input.stopPrice, entryRef)
        })
    }
    // A market entry already sits at LTP; only show LTP separately against a limit entry.
    if (input.ltpRupees != null && !input.entryIsMarket) {
        rows.push({
            role: 'LTP',
            label: 'LTP',
            price: input.ltpRupees,
            priceLabel: formatOrderPriceRupees(input.ltpRupees),
            distanceLabel: distanceLabel(input.ltpRupees, entryRef)
        })
    }

    // Highest price first. Unknown entry price sits between stop and target by construction.
    const fallbackRank: Record<LadderRole, number> = isBuy ? { TARGET: 3, LTP: 2, ENTRY: 2, STOP: 1 } : { STOP: 3, LTP: 2, ENTRY: 2, TARGET: 1 }
    rows.sort((a, b) => {
        if (a.price != null && b.price != null && a.price !== b.price) return b.price - a.price
        return fallbackRank[b.role] - fallbackRank[a.role]
    })

    let riskRupees: number | null = null
    let rewardRupees: number | null = null
    if (entryRef != null && input.quantity > 0) {
        if (input.stopPrice != null) {
            const perUnit = isBuy ? entryRef - input.stopPrice : input.stopPrice - entryRef
            riskRupees = perUnit > 0 ? perUnit * input.quantity : null
        }
        if (input.targetPrice != null) {
            const perUnit = isBuy ? input.targetPrice - entryRef : entryRef - input.targetPrice
            rewardRupees = perUnit > 0 ? perUnit * input.quantity : null
        }
    }
    const ratioLabel = riskRupees != null && rewardRupees != null && riskRupees > 0 ? `1 : ${(rewardRupees / riskRupees).toFixed(1)}` : null

    return { rows, riskRupees, rewardRupees, ratioLabel }
}
